/**
 * A place in the top bar a screen can put its scope into — brief part 1 §6, designer item 2.7:
 * "il run va nella barra in alto".
 *
 * The bar is the chrome's (`AppChrome`), and the run a screen is about is the screen's: the chrome
 * does not know what a run is, and must not learn (it would diverge the day a chapter adds one —
 * the reason its generic scope chips print values and interpret nothing). So the chrome offers an
 * empty element in the bar and the screen portals its own chips into it, the pattern the router
 * already uses for a page's action and headline slots.
 *
 * Outside the chrome (a test that mounts a screen alone) there is no slot and `TopBarPortal`
 * renders nothing — the scope is never drawn into the page body instead, which is what the
 * designer asked to move out of it.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

const TopBarSlot = createContext<HTMLElement | null>(null);

/** Provided by `AppChrome` around its content, with the bar's element once it is mounted. */
export function TopBarSlotProvider({ slot, children }: {
  readonly slot: HTMLElement | null;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <TopBarSlot.Provider value={slot}>{children}</TopBarSlot.Provider>;
}

/** Renders `children` into the top bar, or nothing where there is no bar. */
export function TopBarPortal({ children }: { readonly children: ReactNode }): React.JSX.Element | null {
  const slot = useContext(TopBarSlot);
  return slot === null ? null : createPortal(children, slot);
}
